// CORE
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import styled from 'styled-components'
import * as React from 'react'
import dynamic from 'next/dynamic'

import { VideoData } from '../../interfaces'

// DYNAMIC COMPONENTS
import { db } from '../../data/FirebaseUtils'
// import { Glow } from '../../components/global/Glow'

const AmplitudeInit = dynamic(() => import('../../components/global/AmplitudeInit'), {
  ssr: false,
})
const MoreVideosFeed = dynamic(() => import('../../components/video/more/MoreVideosFeed'), {
  ssr: true,
})
const Layout = dynamic(() => import('../../components/global/Layout'))
const LiquidVideoDetails = dynamic(
  () => import('../../components/video/details/VideoDetails/VideoDetails')
)
const MonetizationDetails = dynamic(
  () => import('../../components/video/details/MonetizationDetails')
)
const SocialDetails = dynamic(() => import('../../components/video/details/SocialDetails'))
const CollectorsDetails = dynamic(() => import('../../components/video/details/CollectorsDetails'))
const VideoPlayer = dynamic(() => import('../../components/video/videoplayer/VideoPlayer'))
const EditionsDetails = dynamic(() => import('../../components/video/editions/EditionsDetails'))

// STYLED COMPONENTS

const VideoWrapper = styled.div`
  position: relative;
  z-index: 1;
  background: #101010;
  display: flex;
  align-items: center;
  flex-direction: column;
  @media (min-width: 43em) {
    padding: 25px;
  }
`

const ContentWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 1600px;
  flex-direction: column;
  /* margin-top: 25px; */
  @media (min-width: 43em) {
    flex-direction: column;
  }
  @media (min-width: 62em) {
    flex-direction: row;
    justify-content: space-between;
  }
`

const DetailsWrapper = styled.div<{ reverseInSmallDesktop?: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.reverseInSmallDesktop ? 'column-reverse' : 'column')};
  padding: 0px;

  width: 100%;
  @media (min-width: 62em) {
    min-width: 50%;
  }
`

const SideWrapper = styled.div`
  display: flex;
  /* position: relative !important; */
  flex-direction: column;
  align-items: flex-start;
  margin: 25px 15px 0 15px;

  @media (min-width: 43em) {
    /* margin: 25px 0 0 0; */
    margin: 0;
    flex-direction: column;
  }

  @media (min-width: 62em) {
    width: 325px;
    margin: 0 0 0 25px;
  }

  @media (min-width: 75em) {
    width: 350px;
  }
`

// const MobileSpacer = styled.div`
//   @media (max-width: 43em) {
//     height: calc(56.25vw);
//   }
// `

const NotFound = styled.div`
  margin-left: 10px;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 85vh;
  font-size: 30px;
  font-weight: 700;
  text-align: center;
`

interface VideoProps {
  video?: VideoData
}

const VideoPage = ({ video }: VideoProps): JSX.Element => {
  // const [sticky, setSticky] = React.useState(false)

  // React.useEffect(() => {
  //   window.addEventListener('scroll', isSticky)
  //   return () => {
  //     window.removeEventListener('scroll', isSticky)
  //   }
  // }, [])

  // const isSticky = () => {
  //   const scrollTop = window.scrollY
  //   const sticky = scrollTop >= 25 ? true : false
  //   setSticky(sticky)
  // }

  if (!video) {
    return (
      <Layout title="No video found.">
        <NotFound>{`This video doesn't exist.`}</NotFound>
      </Layout>
    )
  }

  return (
    <Layout
      title={video.title}
      description={video.description}
      image={`https://glassimg.imgix.net/${video.thumbnail_uri}?ar=16:9&fit=crop&fm=webp&w=1200&h=675`}
      preview={`https://us.glassapi.xyz/${video.mp4_uri}`}
      url={`https://glass.xyz/watch/${video.address}`}
    >
      <AmplitudeInit page="Video" />
      <VideoWrapper>
        {/* <Glow /> */}
        <ContentWrapper>
          <DetailsWrapper>
            <VideoPlayer videoMetadata={video} cardMode={false} />
            {/* {sticky && <MobileSpacer />} */}
            <LiquidVideoDetails video={video} />
            <MonetizationDetails video={video} />
            <CollectorsDetails video={video} />
            <SocialDetails video={video} />
          </DetailsWrapper>
          <SideWrapper>
            <EditionsDetails video={video} />
            <MoreVideosFeed video={video} />
          </SideWrapper>
        </ContentWrapper>
      </VideoWrapper>
    </Layout>
  )
}

export default VideoPage

export const getServerSideProps = async (
  context: GetServerSidePropsContext<{ address: string }>
): Promise<GetServerSidePropsResult<{ video: VideoData | undefined }>> => {
  context.res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=21600')
  const { address = '' } = context.params || {}

  let video: VideoData | undefined

  try {
    const videoDoc = await db.videos.doc(address).get()
    const videoData = videoDoc.data()
    if (!videoData) {
      throw new Error('Video not found')
    }
    const uuid = videoData['video_uuid']
    const transcodingDoc = await db.transcoding.doc(uuid).get()
    const transcodingData = transcodingDoc.data()
    if (!transcodingData) {
      throw new Error('Transcoding not found')
    }

    const presaleCollection = await db.presales(address).get()

    video = {
      id: '', // just a placeholder
      address,
      title: videoData['title'],
      description: videoData['description'],
      created_at: videoData['created_at'] ? new Date(videoData['created_at']) : new Date(),
      created_by: videoData['created_by'],
      audio: transcodingData['audio'],
      width: transcodingData['width'],
      height: transcodingData['height'],
      duration: transcodingData['duration'],
      preview_duration: transcodingData['preview_duration'],
      video_uri: transcodingData['video_uri'],
      trailer_uri: transcodingData['trailer_uri'],
      gated: transcodingData['gated'],
      thumbnail_uri: transcodingData['thumbnail_uri'],
      activity_id: videoData['activity_id'],
      mp4_uri: transcodingData['mp4_uri'],
      has_presale: !presaleCollection.empty,
    }

    console.log(video)
  } catch (e) {
    console.error(e)
    video = undefined
  }

  return { props: { video } }
}
